import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../config/firebase';
import { addDocument, queryDocuments } from './dbService';
import { getDoc, doc } from 'firebase/firestore';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// הפונקציה תבדוק אם המשתמש הנוכחי הוא אדמין
export const checkIsCurrentUserAdmin = async () => {
  try {
    // בדיקה אם המשתמש מחובר
    const user = auth.currentUser;
    if (!user) {
      return false;
    }

    // קריאה לפונקציית הענן שבודקת את הסטטוס של האדמין
    const checkAdminStatusFunction = httpsCallable(functions, 'checkAdminStatus');
    const result = await checkAdminStatusFunction();
    return result.data.isAdmin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// הפונקציה תבדוק את מצב האדמין לפי UID
export const checkIsUserAdmin = async (uid) => {
  try {
    // קריאה לפונקציית הענן שבודקת את הסטטוס של האדמין
    const checkAdminStatusFunction = httpsCallable(functions, 'checkAdminStatus');
    const result = await checkAdminStatusFunction({ uid });
    return result.data.isAdmin === true;
  } catch (error) {
    console.error('Error checking admin status for user:', error);
    return false;
  }
};

// הפונקציה תוסיף הרשאות אדמין למשתמש
export const addAdminRole = async (email) => {
  try {
    const addAdminRoleFunction = httpsCallable(functions, 'addAdminRole');
    const result = await addAdminRoleFunction({ email });
    return result.data;
  } catch (error) {
    console.error('Error adding admin role:', error);
    throw error;
  }
};

// הפונקציה תסיר הרשאות אדמין ממשתמש
export const removeAdminRole = async (uid) => {
  try {
    const removeAdminRoleFunction = httpsCallable(functions, 'removeAdminRole');
    const result = await removeAdminRoleFunction({ uid });
    return result.data;
  } catch (error) {
    console.error('Error removing admin role:', error);
    throw error;
  }
};

// הפונקציה תקבל רשימה של כל האדמינים במערכת
export const getAllAdmins = async () => {
  try {
    const listAdminsFunction = httpsCallable(functions, 'listAdmins');
    const result = await listAdminsFunction();
    const admins = result.data.admins || [];
    
    // מיון המנהלים לפי תאריך הוספה - מהישן ביותר (למעלה) לחדש ביותר (למטה)
    const sortedAdmins = admins.sort((a, b) => {
      // אם חסר תאריך addedAt, מציב כערך מקסימלי (יופיע בסוף)
      if (!a.addedAt) return 1;
      if (!b.addedAt) return -1;
      
      // מיון מהקטן לגדול (ישן לחדש)
      return a.addedAt - b.addedAt;
    });
    
    // סימון שני האדמינים הוותיקים ביותר (שני הראשונים אחרי המיון לפי תאריך)
    // אלה האדמינים שלא ניתן למחוק
    return sortedAdmins.map((admin, index) => ({
      ...admin,
      isOldestAdmin: index < 2  // שני האדמינים הראשונים ברשימה הממוינת
    }));
  } catch (error) {
    console.error('Error getting admins list:', error);
    return [];
  }
};

// פונקציה שבודקת אם הרשאות אדמין נטענו כבר
export const refreshAdminStatus = async () => {
  try {
    // גורם למשתמש לעשות refresh לטוקן שלו, זה יגרום לקליימים להתעדכן
    await auth.currentUser?.getIdToken(true);
    // בודק את סטטוס האדמין המעודכן
    return await checkIsCurrentUserAdmin();
  } catch (error) {
    console.error('Error refreshing admin status:', error);
    return false;
  }
};

// פונקציה ליצירת אדמין ראשון במערכת
export const createFirstAdmin = async () => {
  try {
    const createFirstAdminFunction = httpsCallable(functions, 'createFirstAdmin');
    const result = await createFirstAdminFunction();
    
    if (result.data.success) {
      // רענן את הטוקן כדי שהקליימים יעודכנו
      await auth.currentUser?.getIdToken(true);
      return {
        success: true,
        message: result.data.message
      };
    } else {
      return {
        success: false,
        message: result.data.message || 'שגיאה ביצירת אדמין ראשון'
      };
    }
  } catch (error) {
    console.error('Error creating first admin:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Get user by ID - combines data from Auth and Firestore if available
 * @param {string} userId - The ID of the user to fetch
 * @returns {Promise<Object>} - User object with combined data
 */
export const getUserById = async (userId) => {
  try {
    console.log('Getting user by ID:', userId);
    // First try to get user from Admin SDK
    const isAdmin = await checkIsCurrentUserAdmin();
    let user = null;
    
    if (isAdmin) {
      try {
        console.log('Fetching user via Admin SDK');
        const getUserFunction = httpsCallable(functions, 'getUserById');
        const result = await getUserFunction({ userId });
        
        if (result.data && result.data.user) {
          user = {
            ...result.data.user,
            isActive: !result.data.user.isDisabled
          };
          console.log('Found user via Admin SDK:', user);
        }
      } catch (error) {
        console.error('Error getting user from Admin SDK:', error);
        // Fall back to Firestore if Admin SDK fails
      }
    }
    
    // If not admin or admin SDK failed, try to get from Firestore
    if (!user) {
      console.log('Fetching user from Firestore');
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        user = {
          uid: userId,
          displayName: userData.displayName || '',
          email: userData.email || '',
          createdAt: userData.createdAt || null,
          lastLogin: userData.lastLogin || null,
          isActive: userData.isActive !== false, // default to true
          photoURL: userData.photoURL || ''
        };
        console.log('Found user in Firestore:', user);
      } else {
        console.log('User not found in Firestore, trying to fetch from auth directly');
        
        // Try to get from auth users collection - some users might not have Firestore records
        const authUsersQuery = query(
          collection(db, 'authUsers'),
          where('uid', '==', userId)
        );
        
        const authUserDocs = await getDocs(authUsersQuery);
        if (!authUserDocs.empty) {
          const authUserData = authUserDocs.docs[0].data();
          user = {
            uid: userId,
            displayName: authUserData.displayName || '',
            email: authUserData.email || '',
            createdAt: authUserData.createdAt || null,
            lastLogin: authUserData.lastLoginAt || null,
            isActive: true, // assume active
            photoURL: authUserData.photoURL || ''
          };
          console.log('Found user in authUsers collection:', user);
        }
        
        // If still not found, try to get from all users list
        if (!user) {
          console.log('User not found in authUsers, attempting to get from all users list');
          const { getAllUsers } = await import('./userService');
          const allUsers = await getAllUsers();
          const foundUser = allUsers.find(u => u.uid === userId);
          
          if (foundUser) {
            user = foundUser;
            console.log('Found user in all users list:', user);
          }
        }
      }
    }
    
    // If user is still not found, return null
    if (!user) {
      console.log('User not found in any sources');
      return null;
    }
    
    // Get application count
    const appsQuery = query(
      collection(db, 'userApps'),
      where('userId', '==', userId)
    );
    
    const appsSnapshot = await getDocs(appsQuery);
    console.log('Found', appsSnapshot.size, 'applications for user');
    
    if (user) {
      user.applicationsCount = appsSnapshot.size;
    }
    
    return user;
  } catch (error) {
    console.error('Error getting user by ID:', error);
    throw error;
  }
}; 